/**
 * Unified 0–100 speed slider: 0 = stopped, 1 = ultra-slow float, 100 = full speed.
 * Maps exponentially so the low end has much finer control than linear 0–100%.
 */

export const MOTION_SPEED_SLIDER_MIN = 0;
export const MOTION_SPEED_SLIDER_MAX = 100;

/** Motion rate at slider 1 — extremely slow drift (0.001% of full). */
export const MOTION_SPEED_ULTRA_SLOW = 0.00001;

export function motionUltraSlowForMax(maxRate = 1) {
  const max = Math.max(MOTION_SPEED_ULTRA_SLOW, Number(maxRate) || 1);
  return Math.min(MOTION_SPEED_ULTRA_SLOW, max * 1e-5);
}

/** Convert slider position (0–100) to motion rate (0 … maxRate). */
export function motionRateFromSlider(sliderValue, maxRate = 1) {
  const slider = Math.round(Number(sliderValue) || 0);
  if (slider <= MOTION_SPEED_SLIDER_MIN) return 0;
  const max = Math.max(MOTION_SPEED_ULTRA_SLOW, Number(maxRate) || 1);
  if (slider >= MOTION_SPEED_SLIDER_MAX) return max;
  const min = motionUltraSlowForMax(max);
  const t = (slider - 1) / (MOTION_SPEED_SLIDER_MAX - 1);
  return min * ((max / min) ** t);
}

/** Convert motion rate (0 … maxRate) to slider position (0–100). */
export function motionSliderFromRate(rate, maxRate = 1) {
  const r = Number(rate);
  if (!Number.isFinite(r) || r <= 0) return MOTION_SPEED_SLIDER_MIN;
  const max = Math.max(MOTION_SPEED_ULTRA_SLOW, Number(maxRate) || 1);
  if (r >= max * 0.98) return MOTION_SPEED_SLIDER_MAX;
  const min = motionUltraSlowForMax(max);
  const clamped = Math.max(min, Math.min(max, r));
  const t = Math.log(clamped / min) / Math.log(max / min);
  return Math.max(
    1,
    Math.min(MOTION_SPEED_SLIDER_MAX, Math.round(1 + t * (MOTION_SPEED_SLIDER_MAX - 1))),
  );
}

export function clampMotionRate(rate, maxRate = 1) {
  const max = Math.max(MOTION_SPEED_ULTRA_SLOW, Number(maxRate) || 1);
  const n = Number(rate);
  if (!Number.isFinite(n)) return max;
  if (n <= 0) return 0;
  return Math.min(max, n);
}

export function formatMotionSpeedLabel(rate, maxRate = 1) {
  const r = clampMotionRate(rate, maxRate);
  if (r <= 0) return 'Stopped';
  const slider = motionSliderFromRate(r, maxRate);
  if (slider >= 100) return '100% · Normal';
  if (slider <= 3) return `${slider}% · Floating`;
  if (slider <= 15) return `${slider}% · Slow float`;
  return `${slider}%`;
}
